/**
 * Usage Tracker - Real-time Quota Tracking
 *
 * PHASE 6: Usage Tracking & Paywall System
 * Updated: Now fetches real quota data from entitlement API
 */

import React, { useEffect, useState } from 'react';
import { entitlementService, type EntitlementStatus } from '../../services/entitlement';

interface UsageTrackerProps {
  onUsageChange: (usage: UsageState) => void;
}

interface UsageState {
  used: number;
  total: number;
  canSubmit: boolean;
  buttonText: string;
  progressColor: string;
  planCode?: string;
  upgradeUrl?: string;
}

export const UsageTracker: React.FC<UsageTrackerProps> = ({ onUsageChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [entitlementStatus, setEntitlementStatus] = useState<EntitlementStatus | null>(null);
  const [isPaywallActive, setIsPaywallActive] = useState(true);

  const [usage, setUsage] = useState<UsageState>(() => {
    // Default state while loading
    return {
      used: 0,
      total: 3,
      canSubmit: true,
      buttonText: 'Submit Prompt',
      progressColor: 'bg-green-500',
      planCode: 'anonymous',
      upgradeUrl: '/signup'
    };
  });

  const buildUsageState = (status: EntitlementStatus): UsageState => {
    const quota = status.quotas.comparisons;
    const quotaColor = entitlementService.getQuotaColor(quota);

    return {
      used: quota.used,
      total: quota.limit,
      canSubmit: quota.allowed,
      buttonText: 'Submit Prompt',
      progressColor: quotaColor === 'green' ? 'bg-green-500' :
                     quotaColor === 'yellow' ? 'bg-yellow-500' :
                     quotaColor === 'amber' ? 'bg-amber-500' :
                     'bg-red-500',
      planCode: status.plan.code,
      upgradeUrl: status.upgradeUrl
    };
  };

  const applyUsage = (status: EntitlementStatus) => {
    const usageFromStatus = buildUsageState(status);
    setUsage(usageFromStatus);
    onUsageChange(usageFromStatus);
  };

  // Fetch real quota data on mount
  useEffect(() => {
    const fetchQuotaStatus = async () => {
      try {
        console.log('[ANON-FLOW] UsageTracker fetching quota status');
        setIsLoading(true);
        const status = await entitlementService.getStatus();

        console.log('[ANON-FLOW] UsageTracker quota fetched', {
          paywallEnabled: status.paywallEnabled,
          hasPlan: !!status.plan,
          planCode: status.plan?.code,
          planName: status.plan?.displayName,
          hasQuotas: !!status.quotas,
          used: status.quotas?.comparisons?.used,
          limit: status.quotas?.comparisons?.limit,
          remaining: status.quotas?.comparisons?.remaining,
          allowed: status.quotas?.comparisons?.allowed,
        });

        // Hide tracker when paywall is disabled and use unlimited defaults for parent state
        if (!status.paywallEnabled) {
          const unlimitedStatus = entitlementService.getUnlimitedDefaults();
          setEntitlementStatus(unlimitedStatus);
          applyUsage(unlimitedStatus);
          setIsPaywallActive(false);
          return;
        }

        // If paywall is enabled but missing plan/quota data, skip rendering tracker
        if (!status.plan || !status.quotas) {
          console.warn('[ANON-FLOW] Missing plan/quota data - hiding usage tracker');
          setIsPaywallActive(false);
          return;
        }

        setEntitlementStatus(status);
        setIsPaywallActive(true);
        applyUsage(status);
      } catch (error: any) {
        console.error('[ANON-FLOW] UsageTracker quota fetch failed', {
          errorMessage: error?.message || String(error),
          errorStack: error?.stack,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          responseData: error?.response?.data
        });
        // Keep default state on error - this is expected for anonymous users
        onUsageChange(usage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotaStatus();
  }, [onUsageChange]);

  // Refresh quota status (to be called after comparison creation)
  const refreshQuota = async () => {
    try {
      if (!isPaywallActive) return;

      const status = await entitlementService.getStatus();
      setEntitlementStatus(status);

      applyUsage(status);
    } catch (error) {
      console.error('Failed to refresh quota:', error);
    }
  };

  // Expose refresh function for parent to use
  React.useImperativeHandle(React.useRef(), () => ({
    refreshQuota
  }));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="usage-loading">
        <div className="animate-pulse">Loading quota...</div>
      </div>
    );
  }

  if (!isPaywallActive) {
    return null;
  }

  if (!usage.canSubmit && usage.used >= usage.total) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4" data-testid="usage-limit-reached">
        <div className="flex items-center gap-3">
          <div className="text-red-400 text-lg">🚫</div>
          <div>
            <h4 className="text-red-400 font-semibold">
              {usage.planCode === 'anonymous' ? 'Daily limit reached' : 'Quota exhausted'}
            </h4>
            <p className="text-red-300 text-sm">
              You've used all {usage.total} {usage.planCode === 'anonymous' ? 'free' : ''} comparisons today
            </p>
          </div>
          <a
            href={usage.upgradeUrl || '/auth/signin'}
            className="ml-auto px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
          >
            {usage.planCode === 'anonymous' ? 'Sign In to Get More' : 'Upgrade Plan'}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-sm text-slate-400" data-testid="usage-meter">
      <div className="flex items-center gap-2">
        <span>
          {usage.total - usage.used} of {usage.total}{' '}
          {usage.planCode === 'anonymous' ? 'free ' : ''}
          prompts left today
        </span>
        {entitlementStatus && (
          <span className="text-xs text-slate-500">
            {entitlementStatus.plan.displayName}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="flex-1 max-w-32">
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${usage.progressColor}`}
            style={{ width: `${(usage.used / usage.total) * 100}%` }}
          />
        </div>
      </div>

      {(usage.used >= usage.total * 0.6 || usage.planCode === 'anonymous') && usage.canSubmit && (
        <a
          href={usage.upgradeUrl || '/auth/signin'}
          className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
        >
          {usage.planCode === 'anonymous' ? 'Sign In →' : 'Upgrade →'}
        </a>
      )}
    </div>
  );
};